// @ts-strict-ignore
import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Avatar from 'antd/lib/avatar';
import Dropdown from 'antd/lib/dropdown';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Popover from 'antd/lib/popover';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import { SettingOutlined, SyncOutlined } from '@ant-design/icons';

import { RootState } from '../../../reducers/reducers';
import {
    selectApp,
    selectAppSettings,
    selectOrg,
    selectAppGroup,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsAppsDemoModeEnabled,
    selectAppsById,
    selectAllAppsIdsInOrg,
} from '../../../selectors/selectors';
import { selectShouldRenderWorkflowsOverviewForApp } from '../../../selectors/summaryViewSelectors';
import {
    selectCompletedReleases,
    selectIntegrationRefreshStatusForIntegrations,
} from '../../../selectors/memoizedSelectors';
import { Destination } from '../../../types/destinations';
import { getAppGroupsForOrgId } from '../../../helpers';
import { AppPlatform } from '../../../types';
import { antGrey, getNavBorderContainerStyle, navIconButtonStyle } from '../../styles/globalStyles';
import { useSidebarColors } from '../../styles/themeMode';
import {
    displayTextForAppPlatform,
    checkAllAppNamesAreSame,
    isLegacyAppDisplay,
} from '../../../helpers/displayHelpers';
import { DestinationCounter } from '../../appHome/DestinationCounter';
import { PlatformIconBox } from '../shared/components';
import routes from '../../../routes';
import { createAppRoute, createAppSettingsRoute, createOrgRoute } from '../../../helpers/routeHelpers';
import IntegrationRefreshPopover from '../../IntegrationRefreshPopover';
import InviteTeamModal from '../../inviteTeam/InviteTeamModal';
import { statusNodeForIntegrationStatus } from '../../../helpers/integrationRefreshDataHelpers';
import { trackAppRouteEvent } from '../../../actions/trackEvent';
import { setWelcomeModalOptions } from '../../../actions/ui';
import * as Analytics from '../../../utils/analytics';
import * as Storage from '../../../helpers/localStorageHelper';
import uniq from 'lodash/uniq';
import { tryFetchPaginatedEntities } from '../../../actions/entities';
import { FetchablePaginatedEntityType } from '../../../entityType';
import { useNavigationContext } from '../../../utils/hooks/useNavigationContext';
import { ProductDropdown } from './ProductDropdown';
import { ReleaseHeaderSection } from './ReleaseHeaderSection';
import { PlatformDropdown } from './PlatformDropdown';
import { DropdownButtonContent } from './DropdownButtonContent';
import { OrgNavigationHeader } from './OrgNavigationHeader';

export interface NavigationHeaderProps {
    orgId: string;
    appId?: string | null;
    releaseId?: string | null;
    releaseIdParam?: string; // URL param - available even before release data is loaded
    isOrgRoute?: boolean;
    isOrgSettingsRoute?: boolean;
}

export const NavigationHeader: React.FC<NavigationHeaderProps> = ({
    orgId,
    appId,
    releaseId,
    releaseIdParam,
    isOrgRoute,
    isOrgSettingsRoute,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const sc = useSidebarColors();

    const { org, app, appSettings } = useSelector(
        (state: RootState) => ({
            org: selectOrg(state, orgId),
            app: selectApp(state, appId),
            appSettings: selectAppSettings(state, appId),
        }),
        shallowEqual,
    );
    const appGroups = useSelector((state: RootState) => getAppGroupsForOrgId(orgId, state));
    const isAppsDemoModeEnabled = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const isWorkflowsMigrated = useSelector((state: RootState) =>
        selectShouldRenderWorkflowsOverviewForApp(state, orgId, appId),
    );

    const destinations = useSelector(
        (state: RootState) => Object.values(selectDestinationInfoByDestinationStepConfigId(state, appId)),
        shallowEqual,
    );

    const combinedRefreshDataStatus = useSelector((state: RootState) =>
        selectIntegrationRefreshStatusForIntegrations(state, appId),
    );

    const [isMainDropdownOpen, setIsMainDropdownOpen] = React.useState(false);
    const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = React.useState(false);
    const [showInviteModal, setShowInviteModal] = React.useState(false);

    const { orgIdParam, appIdParam, isAppSettingsRoute } = useNavigationContext();

    const hasCompletedReleases = useSelector((state: RootState) =>
        appId ? selectCompletedReleases(state, appId).length > 0 : false,
    );

    React.useEffect(() => {
        if (appId && orgIdParam && appIdParam && !isAppsDemoModeEnabled && !hasCompletedReleases) {
            dispatch(
                tryFetchPaginatedEntities(
                    FetchablePaginatedEntityType.Releases,
                    { orgId: org?.orgId || orgIdParam, appId, orgIdParam, appIdParam },
                    10,
                    0,
                    appId,
                ),
            );
        }
    }, [appId, orgIdParam, appIdParam, org?.orgId, dispatch, hasCompletedReleases]);

    const appIds = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, org.id));
    const allApps = useSelector((state: RootState) => selectAppsById(state, appIds));
    const allAppsById = React.useMemo(() => allApps.reduce((acc, app) => ({ ...acc, [app.id]: app }), {}), [allApps]);

    const currentAppGroupId = React.useMemo(() => {
        if (!appGroups) return null;
        const group = appGroups.find((g) => app && g.appIds?.includes(app.id));
        return group?.id ?? null;
    }, [appGroups, app?.id]);

    const appsInCurrentGroup = React.useMemo(() => {
        if (!currentAppGroupId || !appGroups) return [];
        const currentGroup = appGroups.find((g) => g.id === currentAppGroupId);
        if (!currentGroup) return [];
        return (currentGroup.appIds ?? []).map((entityId: string) => allAppsById[entityId]).filter(Boolean);
    }, [appGroups, currentAppGroupId, allAppsById]);

    const hasMultipleAppsInGroup = appsInCurrentGroup.length > 1;

    const allAppNamesAreSame = React.useMemo(() => checkAllAppNamesAreSame(appsInCurrentGroup), [appsInCurrentGroup]);

    const isLegacyApp = isLegacyAppDisplay(isWorkflowsMigrated, allAppNamesAreSame);

    const currentAppGroup = useSelector((state: RootState) =>
        currentAppGroupId ? selectAppGroup(state, currentAppGroupId) : null,
    );

    const primaryPlatform = React.useMemo(() => {
        if (!appSettings) return AppPlatform.Ios;
        if (isWorkflowsMigrated && destinations.length > 0) {
            const uniquePlatforms = uniq(destinations.map((d: Destination) => d.platform));
            return uniquePlatforms[0] || appSettings.platform;
        }
        return appSettings.platform;
    }, [appSettings, isWorkflowsMigrated, destinations]);

    const platformDisplayText = React.useMemo(() => {
        if (!appSettings) return '';
        if (!isWorkflowsMigrated || destinations.length === 0) {
            return displayTextForAppPlatform(appSettings.platform, appSettings.appStoreType);
        }
        return uniq(destinations.map((d: Destination) => d.platform))
            .map((p) => displayTextForAppPlatform(p))
            .join(' / ');
    }, [appSettings, isWorkflowsMigrated, destinations]);

    const platformDataByAppId = useSelector(
        (state: RootState) =>
            appsInCurrentGroup.reduce(
                (acc, app) => {
                    acc[app.id] = {
                        destinations: Object.values(selectDestinationInfoByDestinationStepConfigId(state, app.id)),
                        isWorkflowsMigrated: selectShouldRenderWorkflowsOverviewForApp(state, orgId, app.id),
                    };
                    return acc;
                },
                {} as Record<string, { destinations: Destination[]; isWorkflowsMigrated: boolean }>,
            ),
        shallowEqual,
    );

    const platformMenuItems = React.useMemo(() => {
        if (!hasMultipleAppsInGroup || !org) return [];
        return appsInCurrentGroup.map((appInGroup) => {
            const { destinations, isWorkflowsMigrated } = platformDataByAppId[appInGroup.id] ?? {
                destinations: [],
                isWorkflowsMigrated: false,
            };
            const isLegacy = isLegacyAppDisplay(isWorkflowsMigrated, allAppNamesAreSame);
            const platformText =
                isWorkflowsMigrated && destinations.length > 0 ?
                    uniq(destinations.map((d: Destination) => d.platform))
                        .map((p) => displayTextForAppPlatform(p as AppPlatform))
                        .join(' / ')
                :   displayTextForAppPlatform(appInGroup.appSettings.platform, appInGroup.appSettings.appStoreType);
            return {
                key: appInGroup.id,
                label: (
                    <Flex align="center" gap={16} style={{ flex: 1, minWidth: 0 }}>
                        {isLegacy ?
                            <Avatar
                                shape="square"
                                size={24}
                                src={appInGroup.appSettings.appImageUrl}
                                style={{ borderRadius: 4, border: 'none', flexShrink: 0 }}
                            >
                                {appInGroup.appSettings.appName?.charAt(0).toUpperCase()}
                            </Avatar>
                        :   <PlatformIconBox platform={appInGroup.appSettings.platform} />}
                        <Text style={{ color: sc.text, fontSize: 14, flex: 1, minWidth: 0 }} ellipsis>
                            {isLegacy ?
                                <>
                                    {appInGroup.appSettings.appName}
                                    <Text style={{ color: sc.platformMuted, fontWeight: 400, marginLeft: 4 }}>
                                        {' '}
                                        | {platformText}
                                    </Text>
                                </>
                            :   platformText}
                        </Text>
                        {destinations.length > 1 && (
                            <DestinationCounter destinations={destinations} size={20} countOnly />
                        )}
                        <Tooltip title="Settings" mouseEnterDelay={0.4}>
                            <SettingOutlined
                                style={{ color: antGrey.grey7, fontSize: 16 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    history.push(createAppSettingsRoute(org.orgId, appInGroup.appId));
                                    setIsPlatformDropdownOpen(false);
                                }}
                            />
                        </Tooltip>
                    </Flex>
                ),
            };
        });
    }, [appsInCurrentGroup, hasMultipleAppsInGroup, history, org, platformDataByAppId]);

    const showPlatformDropdown = hasMultipleAppsInGroup && platformMenuItems.length > 0;

    const handleAddProduct = React.useCallback(() => {
        dispatch(setWelcomeModalOptions({ shouldShowWelcomeModal: true, shouldDirectlyShowNewAppFlow: true }));
        setIsMainDropdownOpen(false);
    }, [dispatch]);

    const navigateToApp = React.useCallback(
        (groupId: string, appIdParam: string) => {
            if (!org) return;
            Storage.setDefaultAppIdParamInGroupId(groupId, appIdParam);
            Storage.setDefaultAppIdParamInOrg(orgId, appIdParam);
            history.push(createAppRoute(org.orgId, appIdParam));
        },
        [orgId, org, history],
    );

    const handleAppGroupSelect = React.useCallback(
        (appGroupIdToSelect: string) => {
            const group = appGroups?.find((g) => g.id === appGroupIdToSelect);
            const firstEntityId = group?.appIds?.[0];
            const firstAppInGroup = firstEntityId ? allAppsById[firstEntityId] : null;
            if (firstAppInGroup) {
                navigateToApp(appGroupIdToSelect, firstAppInGroup.appId);
            }
        },
        [appGroups, allAppsById, navigateToApp],
    );

    const handlePlatformSelect = React.useCallback(
        (appEntityId: string, appGroupId: string) => {
            const targetGroupId = appGroupId;
            if (!targetGroupId) return;

            const targetGroup = appGroups?.find((g) => g.id === targetGroupId);
            if (!targetGroup) return;

            const appsInGroup = targetGroup.appIds.map((entityId: string) => allAppsById[entityId]).filter(Boolean);
            const selectedApp = appsInGroup.find((a) => a.id === appEntityId);
            if (selectedApp) {
                navigateToApp(targetGroupId, selectedApp.appId);
            }
        },
        [appGroups, allAppsById, navigateToApp],
    );

    const hasAppContext = !!(appId && app && appSettings);

    const renderProductDropdown = React.useCallback(
        () => (
            <ProductDropdown
                orgId={orgId}
                currentAppGroupId={hasAppContext ? currentAppGroupId : null}
                currentAppId={hasAppContext ? app?.appId : undefined}
                currentAppGroupAllAppNamesAreSame={hasAppContext ? allAppNamesAreSame : undefined}
                onAppGroupSelect={(appGroupIdToSelect) => {
                    handleAppGroupSelect(appGroupIdToSelect);
                    setIsMainDropdownOpen(false);
                }}
                onPlatformSelect={(appEntityId, appGroupIdToSelect) => {
                    handlePlatformSelect(appEntityId, appGroupIdToSelect);
                    setIsMainDropdownOpen(false);
                }}
                onSettingsClick={(appIdParam) => {
                    if (!org) return;
                    history.push(createAppSettingsRoute(org.orgId, appIdParam));
                    setIsMainDropdownOpen(false);
                }}
                onAddProduct={handleAddProduct}
                onOrgClick={() => {
                    if (!org) return;
                    history.push(createOrgRoute(org.orgId));
                    setIsMainDropdownOpen(false);
                }}
                onUserClick={() => {
                    history.push(routes.user);
                    setIsMainDropdownOpen(false);
                }}
                onInviteClick={() => {
                    setShowInviteModal(true);
                    setIsMainDropdownOpen(false);
                }}
            />
        ),
        [
            orgId,
            hasAppContext,
            currentAppGroupId,
            app?.appId,
            handleAppGroupSelect,
            handlePlatformSelect,
            handleAddProduct,
            history,
            org,
        ],
    );

    if (!org) return null;

    const appDisplayText = currentAppGroup?.name ?? '';

    const trackIntegrationsRefreshModuleOpened = () => {
        dispatch(
            trackAppRouteEvent({
                orgId,
                appId,
                eventName: Analytics.Event.ViewIntegrationRefreshModule,
            }),
        );
    };

    if (!hasAppContext) {
        return (
            <OrgNavigationHeader
                orgId={orgId}
                orgIdParam={org.orgId}
                orgName={org.name}
                orgImageUrl={org.orgImageUrl}
                isOrgRoute={isOrgRoute}
                isOrgSettingsRoute={isOrgSettingsRoute}
                isMainDropdownOpen={isMainDropdownOpen}
                onMainDropdownOpenChange={setIsMainDropdownOpen}
                renderProductDropdown={renderProductDropdown}
                showInviteModal={showInviteModal}
                onInviteModalClose={() => setShowInviteModal(false)}
            />
        );
    }

    return (
        <div style={{ backgroundColor: sc.bg, padding: '8px', paddingBottom: 0 }}>
            <div style={getNavBorderContainerStyle(sc)}>
                <Flex>
                    <Flex vertical flex={1} style={{ minWidth: 0 }}>
                        <Dropdown
                            open={isMainDropdownOpen}
                            onOpenChange={setIsMainDropdownOpen}
                            dropdownRender={renderProductDropdown}
                            trigger={['click']}
                        >
                            <div>
                                <DropdownButtonContent
                                    icon={
                                        <Avatar
                                            shape="square"
                                            size={30}
                                            src={currentAppGroup?.appGroupImageUrl}
                                            style={{ borderRadius: 8, flexShrink: 0 }}
                                        >
                                            {appDisplayText?.charAt(0).toUpperCase()}
                                        </Avatar>
                                    }
                                    text={appDisplayText}
                                    showCaret
                                    textStyle={{ fontWeight: 600 }}
                                />
                            </div>
                        </Dropdown>

                        <div style={{ borderTop: `1px solid ${sc.border}` }}>
                            <PlatformDropdown
                                primaryPlatform={primaryPlatform}
                                platformDisplayText={platformDisplayText}
                                showPlatformDropdown={showPlatformDropdown}
                                platformMenuItems={platformMenuItems}
                                selectedKey={app?.id}
                                isOpen={isPlatformDropdownOpen}
                                onOpenChange={setIsPlatformDropdownOpen}
                                onPlatformSelect={(key) =>
                                    currentAppGroupId && handlePlatformSelect(key, currentAppGroupId)
                                }
                                onSettingsClick={() => history.push(createAppSettingsRoute(orgId, appId))}
                                isLegacyApp={isLegacyApp}
                                appImageUrl={appSettings?.appImageUrl}
                                appName={appSettings?.appName}
                                destinations={destinations}
                            />
                        </div>
                    </Flex>

                    {!isAppsDemoModeEnabled && (
                        <Flex
                            vertical
                            align="center"
                            justify="center"
                            gap={12}
                            style={{
                                borderLeft: `1px solid ${sc.border}`,
                                padding: '0 8px',
                                backgroundColor: sc.headerControlsBg,
                            }}
                        >
                            <div style={{ position: 'relative' }}>
                                <Popover
                                    placement="bottomLeft"
                                    arrow={{ pointAtCenter: true }}
                                    content={<IntegrationRefreshPopover orgId={orgId} appId={appId} />}
                                    overlayClassName="integration-refresh-popover"
                                    destroyTooltipOnHide
                                    onOpenChange={(open) => {
                                        if (open) {
                                            trackIntegrationsRefreshModuleOpened();
                                        }
                                    }}
                                >
                                    <Button
                                        type="text"
                                        className="product-navigator-icon-btn"
                                        icon={<SyncOutlined style={{ fontSize: 16 }} />}
                                        style={navIconButtonStyle({ icon: sc.icon })}
                                    />
                                </Popover>
                                <div style={{ position: 'absolute', top: -2, right: -2 }}>
                                    {statusNodeForIntegrationStatus(combinedRefreshDataStatus)}
                                </div>
                            </div>

                            {!isAppSettingsRoute && (
                                <Tooltip title="Settings" mouseEnterDelay={0.4}>
                                    <Button
                                        type="text"
                                        className="product-navigator-icon-btn"
                                        icon={<SettingOutlined style={{ fontSize: 16 }} />}
                                        style={navIconButtonStyle({ icon: sc.icon })}
                                        onClick={() => history.push(createAppSettingsRoute(orgId, appId))}
                                    />
                                </Tooltip>
                            )}
                        </Flex>
                    )}
                </Flex>

                {releaseIdParam && (
                    <ReleaseHeaderSection
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        releaseIdParam={releaseIdParam}
                    />
                )}
            </div>

            <InviteTeamModal orgId={orgId} open={showInviteModal} onCancel={() => setShowInviteModal(false)} />
        </div>
    );
};
